import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Card, CardContent, Grid,
  TextField, MenuItem, LinearProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip, Divider,
} from '@mui/material';
import { Receipt, Add, Send, CheckCircle, Cancel, Close, Delete, Paid } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  draft:     { color: 'default', label: '📝 Draft' },
  submitted: { color: 'warning', label: '⏳ Submitted' },
  approved:  { color: 'info',    label: '✅ Approved' },
  rejected:  { color: 'error',   label: '❌ Rejected' },
  paid:      { color: 'success', label: '💰 Paid' },
  cancelled: { color: 'default', label: '🚫 Cancelled' },
};

const CATEGORIES = ['travel','accommodation','meals','transport','entertainment','office_supplies','communication','training','client','other'];
const CURRENCIES = ['HKD','USD','CNY','EUR','GBP','JPY','SGD','AUD'];

const fmtHKD = (n: number) => `HK$${Number(n).toLocaleString('en-HK', { minimumFractionDigits: 2 })}`;

interface Report { id: string; reportNumber: string; submittedById: string; title: string; description?: string; tripStartDate?: string; tripEndDate?: string; currency: string; totalAmount: number; status: string; submittedAt?: string; approvedAt?: string; paidAt?: string; approverNote?: string; submittedBy?: { firstName: string; lastName: string }; approvedBy?: { firstName: string; lastName: string }; items?: LineItem[]; createdAt?: string; }
interface LineItem { id: string; reportId: string; category: string; description: string; date: string; amount: number; currency: string; exchangeRate: number; amountHKD: number; merchant?: string; isReimbursable: boolean; }

const emptyReport = () => ({ title: '', currency: 'HKD', description: '' });
const emptyItem = () => ({ category: 'meals', description: '', date: new Date().toISOString().slice(0, 10), amount: 0, currency: 'HKD', exchangeRate: 1, merchant: '', isReimbursable: true });

export default function ExpenseReports() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager', 'finance'].includes(user?.role ?? '');

  const [reports, setReports] = useState<Report[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [createDlg, setCreateDlg] = useState<any>(null);
  const [detailDlg, setDetailDlg] = useState<Report | null>(null);
  const [addItemDlg, setAddItemDlg] = useState<{ reportId: string; item: any } | null>(null);
  const [reviewDlg, setReviewDlg] = useState<{ id: string; action: 'approve' | 'reject'; note: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [data, st] = await Promise.all([
        apiFetch(`/api/expense-reports${statusFilter ? `?status=${statusFilter}` : ''}`),
        apiFetch('/api/expense-reports/stats'),
      ]);
      setReports(data || []); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (r: Report) => {
    const full = await apiFetch(`/api/expense-reports/${r.id}`);
    setDetailDlg(full);
  };

  const createReport = async () => {
    if (!createDlg?.title) return;
    try {
      await apiFetch('/api/expense-reports', { method: 'POST', body: JSON.stringify(createDlg) });
      setCreateDlg(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const addItem = async () => {
    if (!addItemDlg) return;
    try {
      const updated = await apiFetch(`/api/expense-reports/${addItemDlg.reportId}/items`, {
        method: 'POST', body: JSON.stringify(addItemDlg.item),
      });
      const full = await apiFetch(`/api/expense-reports/${addItemDlg.reportId}`);
      setDetailDlg(full);
      setAddItemDlg(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const removeItem = async (reportId: string, itemId: string) => {
    try {
      await apiFetch(`/api/expense-reports/${reportId}/items/${itemId}`, { method: 'DELETE' });
      const full = await apiFetch(`/api/expense-reports/${reportId}`);
      setDetailDlg(full); load();
    } catch (e: any) { setError(e.message); }
  };

  const submitReport = async (id: string) => {
    try {
      const updated = await apiFetch(`/api/expense-reports/${id}/submit`, { method: 'PATCH' });
      setDetailDlg(updated); load();
    } catch (e: any) { setError(e.message); }
  };

  const review = async () => {
    if (!reviewDlg) return;
    try {
      const updated = await apiFetch(`/api/expense-reports/${reviewDlg.id}/${reviewDlg.action}`, {
        method: 'PATCH', body: JSON.stringify({ approverNote: reviewDlg.note }),
      });
      setReviewDlg(null);
      if (detailDlg?.id === reviewDlg.id) setDetailDlg(updated);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const markPaid = async (id: string) => {
    try {
      const updated = await apiFetch(`/api/expense-reports/${id}/paid`, { method: 'PATCH' });
      if (detailDlg?.id === id) setDetailDlg(updated);
      load();
    } catch (e: any) { setError(e.message); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Expense Reports</Typography>
            <Typography variant="body2" color="text.secondary">Submit and track reimbursable business expenses</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDlg(emptyReport())}>New Report</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Reports', value: stats.total, color: '#37474f' },
            { label: 'Submitted',     value: stats.submitted, color: '#e65100' },
            { label: 'Approved',      value: stats.approved, color: '#1565c0' },
            { label: 'Paid Out',      value: stats.paid, color: '#2e7d32' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {stats && (stats.totalPaidHKD > 0 || stats.pendingAmountHKD > 0) && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#e8eaf6' }}>
          <Typography variant="body2" fontWeight={600} color="primary">
            💰 YTD Paid: {fmtHKD(stats.totalPaidHKD)} · ⏳ Pending: {fmtHKD(stats.pendingAmountHKD)}
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {['', 'draft', 'submitted', 'approved', 'rejected', 'paid'].map(s => (
          <Chip key={s} label={s ? STATUS_CFG[s]?.label : 'All'} size="small"
            onClick={() => setStatusFilter(s)} color={statusFilter === s ? 'primary' : 'default'}
            variant={statusFilter === s ? 'filled' : 'outlined'} />
        ))}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><b>Report #</b></TableCell>
              {isHR && <TableCell><b>Submitted By</b></TableCell>}
              <TableCell><b>Title</b></TableCell>
              <TableCell><b>Trip Dates</b></TableCell>
              <TableCell align="right"><b>Total (HKD)</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map(r => (
              <TableRow key={r.id} hover>
                <TableCell><Typography variant="caption" fontFamily="monospace" color="text.secondary">{r.reportNumber}</Typography></TableCell>
                {isHR && <TableCell><Typography variant="body2">{r.submittedBy?.firstName} {r.submittedBy?.lastName}</Typography></TableCell>}
                <TableCell><Typography variant="body2" fontWeight={600}>{r.title}</Typography></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {r.tripStartDate ? `${r.tripStartDate} → ${r.tripEndDate ?? '…'}` : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right"><Typography fontWeight={700} color={r.totalAmount > 0 ? '#1a237e' : 'text.secondary'}>{fmtHKD(r.totalAmount)}</Typography></TableCell>
                <TableCell><Chip label={STATUS_CFG[r.status]?.label} size="small" color={STATUS_CFG[r.status]?.color} /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View"><IconButton size="small" onClick={() => openDetail(r)}><Receipt fontSize="small" /></IconButton></Tooltip>
                    {r.status === 'draft' && <Tooltip title="Submit"><IconButton size="small" color="primary" onClick={() => submitReport(r.id)}><Send fontSize="small" /></IconButton></Tooltip>}
                    {isHR && r.status === 'submitted' && (
                      <>
                        <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => setReviewDlg({ id: r.id, action: 'approve', note: '' })}><CheckCircle fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => setReviewDlg({ id: r.id, action: 'reject', note: '' })}><Cancel fontSize="small" /></IconButton></Tooltip>
                      </>
                    )}
                    {isHR && r.status === 'approved' && <Tooltip title="Mark Paid"><IconButton size="small" color="success" onClick={() => markPaid(r.id)}><Paid fontSize="small" /></IconButton></Tooltip>}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {reports.length === 0 && !loading && (
              <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No expense reports yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── CREATE DIALOG ── */}
      <Dialog open={!!createDlg} onClose={() => setCreateDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>New Expense Report<IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setCreateDlg(null)}><Close /></IconButton></DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Report Title *" fullWidth value={createDlg?.title ?? ''} onChange={e => setCreateDlg((p: any) => ({ ...p, title: e.target.value }))} />
            <TextField label="Description" fullWidth multiline rows={2} value={createDlg?.description ?? ''} onChange={e => setCreateDlg((p: any) => ({ ...p, description: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Trip Start" type="date" fullWidth InputLabelProps={{ shrink: true }} value={createDlg?.tripStartDate ?? ''} onChange={e => setCreateDlg((p: any) => ({ ...p, tripStartDate: e.target.value }))} />
              <TextField label="Trip End" type="date" fullWidth InputLabelProps={{ shrink: true }} value={createDlg?.tripEndDate ?? ''} onChange={e => setCreateDlg((p: any) => ({ ...p, tripEndDate: e.target.value }))} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDlg(null)}>Cancel</Button>
          <Button variant="contained" onClick={createReport} disabled={!createDlg?.title?.trim()}>Create Draft</Button>
        </DialogActions>
      </Dialog>

      {/* ── DETAIL DIALOG ── */}
      <Dialog open={!!detailDlg} onClose={() => setDetailDlg(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" fontFamily="monospace" color="text.secondary">{detailDlg?.reportNumber}</Typography>
            <Typography fontWeight={700}>{detailDlg?.title}</Typography>
            {detailDlg && <Chip label={STATUS_CFG[detailDlg.status]?.label} size="small" color={STATUS_CFG[detailDlg.status]?.color} />}
          </Box>
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setDetailDlg(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailDlg && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2"><b>Submitted by:</b> {detailDlg.submittedBy?.firstName} {detailDlg.submittedBy?.lastName}</Typography>
                {detailDlg.tripStartDate && <Typography variant="body2"><b>Trip:</b> {detailDlg.tripStartDate} → {detailDlg.tripEndDate ?? '…'}</Typography>}
                {detailDlg.approverNote && <Typography variant="body2" color="error.main"><b>Note:</b> {detailDlg.approverNote}</Typography>}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography fontWeight={700}>Line Items</Typography>
                {detailDlg.status === 'draft' && (
                  <Button size="small" startIcon={<Add />} onClick={() => setAddItemDlg({ reportId: detailDlg.id, item: emptyItem() })}>Add Item</Button>
                )}
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                      <TableCell>Date</TableCell><TableCell>Category</TableCell><TableCell>Description</TableCell>
                      <TableCell>Merchant</TableCell><TableCell align="right">Amount</TableCell>
                      <TableCell align="right">HKD</TableCell>
                      {detailDlg.status === 'draft' && <TableCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailDlg.items ?? []).map(item => (
                      <TableRow key={item.id}>
                        <TableCell><Typography variant="caption">{item.date}</Typography></TableCell>
                        <TableCell><Chip label={item.category.replace('_', ' ')} size="small" variant="outlined" /></TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{item.merchant || '—'}</Typography></TableCell>
                        <TableCell align="right">{item.currency} {Number(item.amount).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtHKD(item.amountHKD)}</TableCell>
                        {detailDlg.status === 'draft' && (
                          <TableCell><IconButton size="small" color="error" onClick={() => removeItem(detailDlg.id, item.id)}><Delete fontSize="small" /></IconButton></TableCell>
                        )}
                      </TableRow>
                    ))}
                    {(detailDlg.items ?? []).length === 0 && (
                      <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>No items yet. Add expenses above.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Paper sx={{ px: 3, py: 1.5, bgcolor: '#e8eaf6' }}>
                  <Typography variant="h6" fontWeight={700} color="primary">Total: {fmtHKD(detailDlg.totalAmount)}</Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {detailDlg?.status === 'draft' && (
            <Button variant="contained" startIcon={<Send />} onClick={() => submitReport(detailDlg!.id)}>Submit Report</Button>
          )}
          {isHR && detailDlg?.status === 'submitted' && (
            <>
              <Button color="success" onClick={() => setReviewDlg({ id: detailDlg!.id, action: 'approve', note: '' })}>Approve</Button>
              <Button color="error" onClick={() => setReviewDlg({ id: detailDlg!.id, action: 'reject', note: '' })}>Reject</Button>
            </>
          )}
          {isHR && detailDlg?.status === 'approved' && (
            <Button color="success" onClick={() => markPaid(detailDlg!.id)}>Mark Paid</Button>
          )}
          <Button onClick={() => setDetailDlg(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── ADD ITEM DIALOG ── */}
      <Dialog open={!!addItemDlg} onClose={() => setAddItemDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Expense Item<IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setAddItemDlg(null)}><Close /></IconButton></DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Category" select fullWidth value={addItemDlg?.item.category ?? 'meals'} onChange={e => setAddItemDlg(p => p ? { ...p, item: { ...p.item, category: e.target.value } } : null)}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
              </TextField>
              <TextField label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={addItemDlg?.item.date ?? ''} onChange={e => setAddItemDlg(p => p ? { ...p, item: { ...p.item, date: e.target.value } } : null)} />
            </Box>
            <TextField label="Description *" fullWidth value={addItemDlg?.item.description ?? ''} onChange={e => setAddItemDlg(p => p ? { ...p, item: { ...p.item, description: e.target.value } } : null)} />
            <TextField label="Merchant" fullWidth value={addItemDlg?.item.merchant ?? ''} onChange={e => setAddItemDlg(p => p ? { ...p, item: { ...p.item, merchant: e.target.value } } : null)} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Amount" type="number" fullWidth value={addItemDlg?.item.amount ?? 0} onChange={e => setAddItemDlg(p => p ? { ...p, item: { ...p.item, amount: Number(e.target.value) } } : null)} />
              <TextField label="Currency" select fullWidth value={addItemDlg?.item.currency ?? 'HKD'} onChange={e => setAddItemDlg(p => p ? { ...p, item: { ...p.item, currency: e.target.value } } : null)}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField label="FX Rate to HKD" type="number" fullWidth value={addItemDlg?.item.exchangeRate ?? 1} onChange={e => setAddItemDlg(p => p ? { ...p, item: { ...p.item, exchangeRate: Number(e.target.value) } } : null)} />
            </Box>
            {addItemDlg && (
              <Typography variant="body2" color="primary" fontWeight={600}>
                ≈ {fmtHKD(Math.round(addItemDlg.item.amount * addItemDlg.item.exchangeRate * 100) / 100)}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemDlg(null)}>Cancel</Button>
          <Button variant="contained" onClick={addItem} disabled={!addItemDlg?.item.description?.trim() || !addItemDlg?.item.amount}>Add Item</Button>
        </DialogActions>
      </Dialog>

      {/* ── REVIEW DIALOG ── */}
      <Dialog open={!!reviewDlg} onClose={() => setReviewDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{reviewDlg?.action === 'approve' ? '✅ Approve Report' : '❌ Reject Report'}</DialogTitle>
        <DialogContent dividers>
          <TextField label={reviewDlg?.action === 'reject' ? 'Rejection reason *' : 'Note (optional)'}
            fullWidth value={reviewDlg?.note ?? ''}
            onChange={e => setReviewDlg(p => p ? { ...p, note: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDlg(null)}>Cancel</Button>
          <Button variant="contained" color={reviewDlg?.action === 'approve' ? 'success' : 'error'}
            disabled={reviewDlg?.action === 'reject' && !reviewDlg?.note?.trim()}
            onClick={review}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
